import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Buildings, Wrench, Cube } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const usd = c => `$${((c || 0) / 100).toFixed(2)}`;

const STATUS_PILL = {
  operational: "bg-emerald-100 text-emerald-800",
  needs_service: "bg-amber-100 text-amber-800",
  broken: "bg-red-100 text-red-800",
};

function AssetCard({ a }) {
  const icon = a.subcategory === "washer" ? "🧺" : a.subcategory === "dryer" ? "🔥" : "📦";
  return (
    <div className={`p-3 bg-white border-2 ${a.is_large_load ? "border-purple-500" : "border-slate-300"}`} data-testid={`asset-${a.asset_id}`}>
      <div className="flex items-center gap-2">
        <div className="text-2xl">{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="font-black text-sm">{a.name}</div>
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
            {a.vendor}{a.location && ` · ${a.location}`}
          </div>
        </div>
        <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${STATUS_PILL[a.status] || STATUS_PILL.operational}`}>{a.status}</span>
      </div>
      {a.is_large_load && (
        <div className="mt-1 inline-block px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-purple-500 text-white">Large Capacity</div>
      )}
    </div>
  );
}

export default function LandlordPropertyDetail() {
  const { token, addressKey } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openUnit, setOpenUnit] = useState(null);
  useEffect(() => {
    axios.get(`${BASE}/api/public/landlord/${token}/property/${encodeURIComponent(addressKey)}`)
      .then(r => setData(r.data))
      .catch(() => toast.error("Couldn't load property"))
      .finally(() => setLoading(false));
  }, [token, addressKey]);

  if (loading) return <div className="min-h-screen bg-slate-100 flex items-center justify-center font-black uppercase text-slate-400 text-xs">Loading…</div>;
  if (!data) return <div className="min-h-screen bg-slate-100 flex items-center justify-center font-black uppercase text-slate-500">Property not found</div>;

  const laundryAssets = (data.assets || []).filter(a => a.category === "laundry");
  const otherAssets = (data.assets || []).filter(a => a.category !== "laundry");
  const residential = (data.units || []).filter(u => u.unit_type !== "commercial" && u.unit_type !== "warehouse");
  const commercial = (data.units || []).filter(u => u.unit_type === "commercial");
  const warehouse = (data.units || []).filter(u => u.unit_type === "warehouse");
  const occupied = residential.filter(u => u.occupied).length;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="landlord-property-detail">
      <header className="bg-[#0f172a] text-white">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <Link to={`/landlord/${token}`} data-testid="prop-back-btn"
            className="text-[10px] font-black uppercase tracking-widest text-amber-500 hover:text-amber-300 inline-flex items-center gap-1">
            <ArrowLeft weight="bold" size={12}/> Back to Portfolio
          </Link>
          <div className="font-display font-black text-2xl mt-1">{data.address}</div>
          <div className="text-xs text-slate-300 mt-0.5">
            {residential.length > 0 && `${residential.length} residential`}
            {commercial.length > 0 && ` · ${commercial.length} commercial`}
            {warehouse.length > 0 && ` · vacant warehouse`}
            {occupied > 0 && ` · ${occupied} occupied`}
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto p-4 space-y-6">
        {(data.building_door_code || data.laundry_room_key_code || data.keys_location_note || (data.units || []).some(u => u.unit_door_code || u.unit_key_code)) && (
          <section data-testid="access-codes-section" className="p-3 bg-white border-2 border-amber-500">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <div className="text-2xl">🔑</div>
              <div className="font-display font-black text-xl uppercase tracking-tight">Access Codes</div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Landlord + PMR only · not shared publicly</div>
            </div>
            {data.keys_location_note && (
              <div className="mb-2 p-2 bg-cyan-50 border-2 border-cyan-400 text-xs" data-testid="landlord-keys-note">
                <span className="text-[10px] font-black uppercase tracking-widest text-cyan-700 mr-1">Keys:</span>
                {data.keys_location_note}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
              {data.building_door_code && (
                <div className="p-2 bg-amber-50 border-2 border-amber-400" data-testid="landlord-building-code">
                  <div className="text-[10px] font-black uppercase tracking-widest text-amber-700">Building Door</div>
                  <div className="font-mono font-black text-2xl text-amber-800 tracking-widest">{data.building_door_code}</div>
                </div>
              )}
              {data.laundry_room_key_code && (
                <div className="p-2 bg-emerald-50 border-2 border-emerald-400" data-testid="landlord-laundry-key">
                  <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Laundry Room Key (A Keyway)</div>
                  <div className="font-mono font-black text-2xl text-emerald-800 tracking-widest">{data.laundry_room_key_code}</div>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {(data.units || []).filter(u => u.unit_door_code || u.unit_key_code || u.unit_note).map(u => (
                <div key={u.unit} className="p-2 bg-white border-2 border-amber-300" data-testid={`landlord-unit-code-${u.unit}`}>
                  <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Unit {u.unit}</div>
                  {u.unit_door_code && (
                    <div className="mt-0.5">
                      <span className="text-[9px] font-black uppercase tracking-widest text-amber-600 mr-1">Push-btn</span>
                      <span className="font-mono font-black text-base text-amber-800 tracking-wider">{u.unit_door_code}</span>
                    </div>
                  )}
                  {u.unit_key_code && (
                    <div className="mt-0.5">
                      <span className="text-[9px] font-black uppercase tracking-widest text-slate-500 mr-1">Key ID</span>
                      <span className="font-mono font-black text-sm text-slate-700 tracking-wider">{u.unit_key_code}</span>
                    </div>
                  )}
                  {u.unit_note && (
                    <div className="mt-1 text-[10px] text-red-700 font-medium italic">⚠ {u.unit_note}</div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {data.project && (
          <section className="p-3 bg-white border-2 border-emerald-600">
            <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Active Project</div>
            <div className="flex items-center justify-between gap-2 mt-1">
              <div>
                <div className="font-display font-black text-lg leading-tight">{data.project.name}</div>
                <div className="text-xs text-slate-600">{data.project.complete}/{data.project.total} done{data.project.pending > 0 && <span className="ml-1 text-orange-600 font-black">· {data.project.pending} pending approval</span>}</div>
              </div>
              {data.project.share_token && (
                <a href={`/project/${data.project.share_token}`} target="_blank" rel="noreferrer"
                  className="h-9 px-3 bg-cyan-600 hover:bg-cyan-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center">🔗 Live Status</a>
              )}
            </div>
          </section>
        )}

        {/* Units */}
        <section data-testid="units-list">
          <div className="flex items-center gap-2 mb-2">
            <Buildings weight="fill" size={20} className="text-fuchsia-600"/>
            <div className="font-display font-black text-xl uppercase tracking-tight">Units ({data.units.length})</div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Tap a unit for its assets</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {data.units.map(u => {
              const unitAssets = (data.assets || []).filter(a => (a.location || "").includes(u.unit) || (a.asset_id || "").endsWith(u.unit));
              const isOpen = openUnit === u.unit;
              return (
                <div key={u.unit} className={`col-span-1 ${isOpen ? "md:col-span-2 lg:col-span-3" : ""}`}>
                  <button onClick={() => setOpenUnit(isOpen ? null : u.unit)}
                    className={`w-full text-left p-2 bg-white border-2 hover:shadow-[3px_3px_0px_#0f172a] transition-shadow ${u.occupied ? "border-red-500" : u.vacancy_status === "lease_signed" ? "border-blue-500" : isOpen ? "border-fuchsia-600" : "border-slate-300"}`}
                    data-testid={`unit-${u.unit}`}>
                    <div className="font-black text-sm">{u.unit}</div>
                    <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">
                      {u.unit_type === "commercial" ? "commercial" : u.unit_type === "warehouse" ? "warehouse" : `${u.br_count}BR`}
                    </div>
                    <div className={`text-[9px] font-black uppercase tracking-widest mt-0.5 ${u.occupied ? "text-red-600" : u.vacancy_status === "lease_signed" ? "text-blue-600" : "text-slate-400"}`}>
                      {u.occupied ? "🚫 occupied" : u.vacancy_status === "lease_signed" ? "signed" : "vacant"}
                    </div>
                    {unitAssets.length > 0 && (
                      <div className="text-[9px] font-black uppercase tracking-widest text-purple-600 mt-1">{unitAssets.length} asset{unitAssets.length !== 1 ? "s" : ""}</div>
                    )}
                  </button>
                  {isOpen && (
                    <div className="mt-1 p-2 bg-fuchsia-50 border-2 border-fuchsia-600 space-y-1" data-testid={`unit-${u.unit}-detail`}>
                      <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-700">
                        Unit {u.unit} — {u.unit_type === "apartment" ? `${u.br_count}BR` : u.unit_type} · {u.occupied ? "occupied" : u.vacancy_status === "lease_signed" ? "signed lease" : "vacant"}
                        {u.head_lessee && ` · leased to ${u.head_lessee}`}
                      </div>
                      {unitAssets.length === 0 ? (
                        <div className="text-[10px] text-slate-500 italic">No assets logged for this unit yet.</div>
                      ) : unitAssets.map(a => (
                        <div key={a.asset_id} className="text-[10px] p-1 bg-white border border-fuchsia-200">
                          <div className="font-black">{a.name}</div>
                          <div className="text-slate-500">{a.subcategory}{a.vendor && ` · ${a.vendor}`}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Laundry Assets */}
        {laundryAssets.length > 0 && (
          <section data-testid="laundry-assets">
            <div className="flex items-center gap-2 mb-2">
              <Cube weight="fill" size={20} className="text-purple-600"/>
              <div className="font-display font-black text-xl uppercase tracking-tight">Laundry Room ({laundryAssets.length} machines)</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {laundryAssets.map(a => <AssetCard key={a.asset_id} a={a}/>)}
            </div>
          </section>
        )}

        {otherAssets.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-2">
              <Cube weight="fill" size={20} className="text-slate-600"/>
              <div className="font-display font-black text-xl uppercase tracking-tight">Other Assets</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {otherAssets.map(a => <AssetCard key={a.asset_id} a={a}/>)}
            </div>
          </section>
        )}

        {/* Work orders */}
        <section data-testid="workorders">
          <div className="flex items-center gap-2 mb-2">
            <Wrench weight="fill" size={20} className="text-blue-600"/>
            <div className="font-display font-black text-xl uppercase tracking-tight">Work Orders ({data.workorders.length})</div>
          </div>
          {data.workorders.length === 0 ? (
            <div className="p-4 bg-white border-2 border-dashed border-slate-300 text-xs text-slate-500 text-center">No tickets yet.</div>
          ) : (
            <div className="space-y-2">
              {data.workorders.map(w => (
                <div key={w.id} className="p-2 bg-white border-2 border-[#0f172a]" data-testid={`wo-${w.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="font-black text-sm">{w.subject}</div>
                    <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-slate-100 border border-slate-300">{w.status}</span>
                  </div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mt-0.5">
                    Unit {w.unit} · {w.category}{w.estimated_cost_cents > 0 && ` · ${usd(w.estimated_cost_cents)}`}
                    {w.needs_approval && <span className="ml-1 text-orange-600">· Leslie CC'd</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
